import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL_NEW } from '@utils/api/endpoints.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { AxiosError } from 'axios';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params.ts';
import { CustomModelDataType } from './use-update-custom-model';
import { CUSTOM_MODEL_QUERY_KEYS } from './query-keys';

export interface CustomModels {
  count: number;
  total_results: number;
  result: ModelData[];
}

// Background-processing state of a single dataset file. Uploads are accepted
// fast (TUS) and processed in the background, so a file appears as PROCESSING
// and later flips to READY or FAILED. Mirrors the API enum.
export enum CustomModelDataStatus {
  PROCESSING = 1,
  READY = 2,
  FAILED = 3
}

export interface ModelData {
  id: number;
  name: string;
  type: CustomModelDataType;
  language_id: number | null;
  media_url: string | null;
  duration: number | null;
  start_time: number | null;
  end_time: number | null;
  url: string;
  // Optional for resilience against responses cached before this field existed;
  // a missing value is treated as READY in the UI.
  status?: CustomModelDataStatus;
  error?: string | null;
  round_number?: number | null;
  // Provenance for editor-contributed clips (#4018/#4020). A row that a
  // customer uploaded directly leaves these NULL; a row cut from a session
  // via "Add to training data" carries the source (nullable because the
  // session can age out on retention — ``ON DELETE SET NULL``) and the
  // corrected term the customer confirmed.
  source_kind?: string | null;
  /** Integer database PK. Do NOT use in URLs — the router expects the
   *  string `zoom_id` on `source_session_zoom_id`. Kept for the
   *  "still exists" check and legacy consumers. */
  source_session_id?: number | null;
  /** Integer database PK. Do NOT use in URLs — the router expects the
   *  UUID on `source_document_uuid`. */
  source_document_id?: number | null;
  /** Routable string session id (`TranscriptSession.zoom_id`). Use this
   *  in `PATHS(...).CAPTIONS` / `.TRANSCRIPT` links. Null if the source
   *  session was aged out by retention. */
  source_session_zoom_id?: string | null;
  /** Routable document UUID (`Document.document_id`). Use with
   *  `source_session_zoom_id` for the caption / transcript link. */
  source_document_uuid?: string | null;
  anchor_text?: string | null;
  created_at?: string | null;
}

export const useGetCustomModelsData = (id: number) => {
  const { searchParams } = useNewAppSearchParams();

  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;

  const sort = (searchParams.sort as string | undefined) || undefined;
  const direction =
    (searchParams.direction as 'asc' | 'desc' | undefined) || undefined;
  const q = (searchParams.q as string | undefined) || undefined;

  const fetchDatasets = async () => {
    const { datasetsByModelId } = GET_URL_NEW();
    const url = datasetsByModelId(id, { offset, limit, q, sort, direction });

    const { data } = await Axios.get<CustomModels>(url);
    return data;
  };

  return useQuery<CustomModels, AxiosError<BaseError>>(
    CUSTOM_MODEL_QUERY_KEYS.dataWithPagination(id, offset, limit),
    fetchDatasets,
    {
      useErrorBoundary: false,
      // Only poll while at least one dataset is still processing (uploads
      // land as PROCESSING and flip to READY/FAILED asynchronously). Once
      // every row is in a terminal state the periodic refetch just burns
      // requests and re-renders the grid.
      refetchInterval: data => {
        const hasPending = data?.result?.some(
          r => r.status === CustomModelDataStatus.PROCESSING
        );
        return hasPending ? 15000 : false;
      },
      refetchIntervalInBackground: true,
      // See note on use-get-custom-models.tsx.
      refetchOnWindowFocus: false,
      staleTime: 8000
    }
  );
};
